import React from 'react';
import { FilterSetController, SelectButton, Filter, Table, ContentCard, Card, DropdownSort, DropdownItem, DropdownLine } from '@weezevent/nacre';
import DesignEmpty from './design-row/designEmpty';
import { DesignModale } from './design-row/designModale';
import DesignModel from '../../../../models/designModel';

import { DesignRow } from './design-row/designRow';
import { handleSearchQuery } from '../../../manager/components/context';
import Style from './templateList.module.css';
import LoadingTable from '../../../../components/loadingTable';
import { typesKeyNames } from './helper';

import { useTranslation } from 'react-i18next';

function DesignHeader({ allSelected, ordering, setOrdering, level }) {
    const { t } = useTranslation();

    const columns = [
        {
            order: 'name',
            label: t('ticket.ticket_design.designs.name'),
            sorting: true
        },
        {
            order: 'template__type',
            label: t('ticket.ticket_design.designs.type'),
            sorting: true
        },
        {
            order: 'template__name',
            label: t('ticket.ticket_design.templates.name'),
            sorting: true
        },
        {
            label: t(`ticket.ticket_design.designs.${level === 'event' ? 'tickets' : 'events'}.title`),
            sorting: false
        },
        {
            class: 'action',
            label: t('ticket.common.cta.actions'),
            sorting: false,
            colspan: 2
        }
    ];

    return (
        <Table.THead>
            <Table.Tr active={allSelected}>
                {columns.map((column, index) => (
                    <th key={index} colSpan={column.colspan || undefined} className={column.class === 'action' ? Style['action-column'] : null}>
                        {column.sorting ? (
                            <div>
                                <DropdownSort
                                    active={ordering === column.order || ordering === `-${column.order}`}
                                    name={column.order}
                                    ordering={ordering}
                                    label={column.label}
                                    items={[
                                        <DropdownItem.Icons
                                            key={1}
                                            item={t('ticket.common.ordering.descending')}
                                            checked={ordering === `-${column.order}`}
                                            onClick={() => setOrdering(`-${column.order}`)}
                                        />,
                                        <DropdownItem.Icons
                                            key={2}
                                            item={t('ticket.common.ordering.ascending')}
                                            checked={ordering === column.order}
                                            onClick={() => setOrdering(column.order)}
                                        />
                                    ]}
                                />
                            </div>
                        ) : (
                            column.label
                        )}
                    </th>
                ))}
            </Table.Tr>
        </Table.THead>
    );
}

const spec = {
    multiple: ['template__type__in'],
    fields: ['template__type__in', 'search']
};

export default function DesignList({ organization, event = null, isReadOnly = false }) {
    const { t } = useTranslation();
    const [ordering, setOrdering] = React.useState('name');
    const [filters, setFilters] = React.useState({});
    const [modaleType, setModaleType] = React.useState({ action: null, selected: [] });
    const [selectedDesigns, setSelectedDesigns] = React.useState({});
    const [allSelected, setAllSelected] = React.useState(false);

    const numberSelected = React.useMemo(() => Object.keys(selectedDesigns)?.length || 0, [selectedDesigns]);

    const typeOptions = Object.entries(typesKeyNames).map(([key, value]) => {
        return {
            key: key,
            name: key,
            text: t(`ticket.ticket_design.type.${value}`)
        };
    });

    const [designs, designsLoading, sync] = DesignModel.useApiModel(
        {
            organizationId: organization.pk,
            launch: Boolean(organization?.pk),
            eventId: event?.pk,
            query: {
                expand: 'template',
                ordering: ordering,
                ...handleSearchQuery(spec, filters, false)
            }
        },
        [organization, filters, ordering, event]
    );

    //@Todo Design ordering en back pour les sous properties de template
    function getTemplateProperty(obj, property) {
        if (property === 'type' && obj['template']) return t(`ticket.ticket_design.type.${typesKeyNames[obj['template'][property]]}`);
        return obj['template'][property];
    }

    //custom sort for template properties
    const sortedDesigns = React.useMemo(() => {
        const reverse = ordering[0] === '-';
        const orders = ordering.substring(reverse ? 1 : 0).split('__');
        //we only do a custom sort if the property is in template
        if (orders.length === 2) {
            if (reverse) return designs?.sort((a, b) => (getTemplateProperty(a, orders[1]) < getTemplateProperty(b, orders[1]) ? -1 : 1));
            return designs?.sort((a, b) => (getTemplateProperty(a, orders[1]) >= getTemplateProperty(b, orders[1]) ? -1 : 1));
        }
        return designs;
    }, [designs, ordering]);

    const resetSelected = React.useCallback(() => {
        setAllSelected(false);
        setSelectedDesigns({});
    });

    const onChangeSearch = React.useCallback(
        value => {
            let tmpFilters = { ...filters };
            if (value !== '' && value !== null) {
                tmpFilters = { ...tmpFilters, search: value };
            } else {
                delete tmpFilters.search;
            }
            setFilters(tmpFilters);
        },
        [filters]
    );

    const handleAllClick = React.useCallback(
        ({ event, data: { checked } }) => {
            let designsTmp = {};
            if (checked) {
                designs.forEach(design => {
                    designsTmp[design.pk] = design;
                });
            }
            setAllSelected(checked);
            setSelectedDesigns(designsTmp);
        },
        [designs]
    );

    const handleElementsList = React.useCallback(
        ({ event, data: { checked } }, template) => {
            setSelectedDesigns(currentState => {
                const newSelected = { ...currentState };
                const isSelected = template.pk in currentState;
                if (!checked && isSelected) {
                    delete newSelected[template.pk];
                }

                if (checked && !isSelected) {
                    newSelected[template.pk] = template;
                }
                setAllSelected(Object.values(newSelected).length === designs.length);

                return newSelected;
            });
        },
        [designs]
    );

    return (
        <Card separator={null}>
            <ContentCard>
                <FilterSetController
                    total={designs?.length}
                    isLoadingTotal={designsLoading}
                    labelTotal={t('ticket.ticket_design.designs.total', { count: designs?.length })}
                    labelReset={t('ticket.common.action.reset')}
                    labelFilters={t('ticket.common.cta.filters')}
                    search={{
                        onChange: (synth, value) => {
                            onChangeSearch(value);
                            resetSelected();
                        },
                        handleReset: (synth, value) => {
                            onChangeSearch(null);
                        },
                        placeholder: t('ticket.ticket_design.designs.search'),
                        value: filters?.search || ''
                    }}
                    onChange={values => {
                        if (filters.search !== undefined && filters.search.length > 0) {
                            values.search = filters.search;
                        }
                        setFilters(values);
                        resetSelected();
                    }}
                    onReset={() => {}}
                    onDelete={key => {}}
                >
                    <Filter
                        name="template__type__in"
                        multiple
                        labelSelectAll={t('ticket.common.action.select-all')}
                        labelName={t('ticket.ticket_design.designs.type')}
                        options={typeOptions}
                        index="0"
                        labelCTA={t('ticket.common.action.apply')}
                    />
                </FilterSetController>
                <div className={Style['global-pagesize-container']}>
                    <SelectButton
                        disabled={numberSelected === 0}
                        tooltipTitle={t('ticket.ticket_design.designs.selection.no_design')}
                        title={numberSelected > 0 ? t('ticket.ticket_design.designs.selection.action', { count: numberSelected }) : t('ticket.common.action.bulk')}
                        objects={numberSelected > 0 ? Object.values(selectedDesigns) : []}
                    >
                        {numberSelected <= 1 && (
                            <>
                                <DropdownItem
                                    disabled={event?.pk && Object.values(selectedDesigns)?.find(d => !d.event_id)}
                                    onClick={() => setModaleType({ action: 'duplicate', selected: Object.values(selectedDesigns) })}
                                >
                                    {t('ticket.common.action.duplicate')}
                                </DropdownItem>
                                <DropdownLine />
                            </>
                        )}
                        <DropdownItem
                            disabled={numberSelected > 1 || (event?.pk && Object.values(selectedDesigns)?.find(d => !d.event_id))}
                            onClick={() => setModaleType({ action: 'delete', selected: Object.values(selectedDesigns) })}
                            deleteItem
                        >
                            {t('ticket.common.action.delete')}
                        </DropdownItem>
                    </SelectButton>
                </div>
            </ContentCard>
            {modaleType.action && (
                <DesignModale
                    event={event}
                    organization={organization}
                    sync={() => sync({ allow_cache: false, cache: false })}
                    setShowModale={setModaleType}
                    designs={modaleType.selected}
                    type={modaleType.action}
                    setSelectedDesigns={setSelectedDesigns}
                    level={event ? 'event' : 'orga'}
                />
            )}
            <Table pk="id" useNew onSelectAll={(event, data) => handleAllClick({ event, data })} selectable className={Style['list__table']}>
                <DesignHeader allSelected={allSelected} ordering={ordering} setOrdering={setOrdering} level={event?.pk ? 'event' : 'orga'} />
                {!designsLoading && sortedDesigns && (
                    <Table.TBody>
                        {sortedDesigns?.length > 0 &&
                            sortedDesigns.map((design, idx) => (
                                <DesignRow
                                    level={event?.pk ? 'event' : 'orga'}
                                    key={idx}
                                    onSelected={handleElementsList}
                                    design={design}
                                    event={event}
                                    organization={organization}
                                    selectedDesigns={selectedDesigns}
                                    setModaleType={setModaleType}
                                    isReadOnly={isReadOnly}
                                />
                            ))}
                    </Table.TBody>
                )}
            </Table>
            <LoadingTable loading={designsLoading} />
            {!designsLoading && designs?.length === 0 && <DesignEmpty event={event} isReadOnly={isReadOnly} />}
        </Card>
    );
}
